import { isTuningCommand } from '@playcastdotio/PeerCore';
import type { ITuningClock, TuningCommand, TuningIdentity, TuningReceipt, TuningSessionLease,
  TuningTrialSnapshot } from '@playcastdotio/PeerCore';

type Session = { snapshot: TuningTrialSnapshot; authority: TuningIdentity;
  lease?: TuningSessionLease; timer?: ReturnType<typeof setTimeout> };
type Intake = { content: string; receipt: TuningReceipt };
export class HostTuningSessionManager {
  private readonly records = new Map<string, Session>();
  private readonly replay = new Map<string, Intake>();
  private readonly trials = new Map<string, TuningTrialSnapshot>();
  constructor(private readonly deps: { clock: ITuningClock; id(): string; eligible(): boolean;
    expire(sessionId: string): Promise<void> }) {}
  public record(snapshot: TuningTrialSnapshot): void {
    const prior = this.records.get(snapshot.target.sessionId);
    const pending = ['preparing', 'replacing', 'connecting', 'restoring', 'failed'].includes(snapshot.phase);
    const proposed = prior && !this.matches(prior.authority, snapshot.target)
      && (pending || snapshot.phase === 'stopped' && snapshot.target.configRevision <= prior.authority.configRevision);
    if (!proposed) this.records.set(snapshot.target.sessionId, { ...prior, snapshot: structuredClone(snapshot),
      authority: prior && pending ? prior.authority : { ...snapshot.target } });
    this.trials.set(snapshot.target.trialId, structuredClone(snapshot));
    while (this.trials.size > 128) this.trials.delete(this.trials.keys().next().value as string);
  }
  public sessions(): readonly TuningTrialSnapshot[] { return [...this.records.keys()].map(id => this.session(id)!); }
  public session(id: string): TuningTrialSnapshot | undefined {
    const record = this.records.get(id);
    return record && structuredClone({ ...record.snapshot, target: record.authority });
  }
  public trial(id: string): TuningTrialSnapshot | undefined { const trial = this.trials.get(id); return trial && structuredClone(trial); }
  public arm(id: string, ttlMs: number): TuningSessionLease {
    const record = this.records.get(id);
    if (!record || !this.deps.eligible()) throw new Error('session-ineligible');
    if (!Number.isFinite(ttlMs) || ttlMs < 60_000 || ttlMs > 900_000) throw new Error('lease-ttl-invalid');
    if (record.snapshot.phase === 'stopped') record.snapshot = { ...record.snapshot, phase: 'idle' };
    if (record.timer) clearTimeout(record.timer);
    record.lease = { sessionId: id, peerId: record.snapshot.target.peerId, leaseId: record.lease?.leaseId ?? this.deps.id(),
      expiresAtHostMonotonicMs: this.deps.clock.monotonicMs() + ttlMs };
    record.timer = setTimeout(() => { void this.deps.expire(id).catch(() => undefined); }, ttlMs);
    return { ...record.lease };
  }
  public lease(id: string): TuningSessionLease | undefined { return this.records.get(id)?.lease; }
  public authority(id: string): TuningIdentity | undefined { return this.records.get(id)?.authority; }
  public extendRun(id: string, leaseId: string, absoluteDeadlineMs: number): TuningSessionLease {
    const record = this.records.get(id); const now = this.deps.clock.monotonicMs();
    if (!record?.lease || record.lease.leaseId !== leaseId || record.lease.expiresAtHostMonotonicMs <= now
      || absoluteDeadlineMs <= now || absoluteDeadlineMs > now + 1_500_000) throw new Error('qualification-renewal-denied');
    if (record.timer) clearTimeout(record.timer);
    record.lease = { ...record.lease, expiresAtHostMonotonicMs: absoluteDeadlineMs };
    record.timer = setTimeout(() => { void this.deps.expire(id).catch(() => undefined); }, absoluteDeadlineMs - now);
    return { ...record.lease };
  }
  public authorized(command: TuningCommand): boolean {
    const record = this.records.get(command.target.sessionId); const lease = record?.lease;
    return isTuningCommand(command) && this.deps.eligible() && !!record && !!lease
      && lease.leaseId === command.leaseId && lease.peerId === command.target.peerId
      && lease.expiresAtHostMonotonicMs > this.deps.clock.monotonicMs() && this.matches(record.authority, command.target);
  }
  public intake(id: string, command: TuningCommand): TuningReceipt {
    const prior = this.replay.get(`${id}:${command.commandId}`);
    const content = JSON.stringify(command);
    if (prior) return prior.content === content ? structuredClone(prior.receipt) : this.reject(command, 'conflicting-command-replay');
    const record = this.records.get(id);
    const lease = record?.lease;
    const now = this.deps.clock.monotonicMs();
    const error = !isTuningCommand(command) ? 'invalid-command' : !this.deps.eligible() ? 'ineligible-host'
      : !record || command.target.sessionId !== id ? 'unknown-session'
      : !lease || lease.leaseId !== command.leaseId || lease.peerId !== command.target.peerId
        || lease.expiresAtHostMonotonicMs <= now ? 'lease-expired'
      : !this.matches(record.authority, command.target) && !(command.action.kind === 'stop'
        && this.matches(record.snapshot.target, command.target)) ? 'stale-target' : undefined;
    const receipt = error ? this.reject(command, error) : { version: 1 as const, commandId: command.commandId,
      target: command.target, stage: 'accepted' as const, settings: [], policyVersion: 'tuning-v1' };
    this.replay.set(`${id}:${command.commandId}`, { content, receipt });
    while (this.replay.size > 512) this.replay.delete(this.replay.keys().next().value as string);
    return receipt;
  }
  public complete(sessionId: string, receipt: TuningReceipt): void {
    const replay = this.replay.get(`${sessionId}:${receipt.commandId}`); if (replay) replay.receipt = receipt;
  }
  public revoke(id: string): void {
    const record = this.records.get(id); if (!record) return;
    if (record.timer) clearTimeout(record.timer); delete record.timer; delete record.lease;
  }
  public remove(id: string): void { this.revoke(id); this.records.delete(id); }
  public dispose(): void { for (const id of this.records.keys()) this.revoke(id); this.records.clear(); this.replay.clear(); this.trials.clear(); }
  private matches(left: TuningIdentity, right: TuningIdentity): boolean {
    return left.peerId === right.peerId && left.trialId === right.trialId && left.connectionEpoch === right.connectionEpoch
      && left.configRevision === right.configRevision;
  }
  private reject(command: TuningCommand, code: string): TuningReceipt {
    return { version: 1, commandId: command.commandId, target: command.target, stage: 'rejected', settings: [],
      error: { code, message: code }, policyVersion: 'tuning-v1' };
  }
}
